<template>
  <section id="formulaire" class="py-5">
    <div class="container">
      <div class="row">
        <div class="col-lg-8 mx-auto">
          <div class="text-center mb-5">
            <h2 class="display-5 fw-bold">Demande de pré-inscription</h2>
            <p class="lead text-muted">
              Remplissez ce formulaire pour initier votre demande. Nous vous recontacterons très prochainement.
            </p>
          </div>

          <!-- We add a @submit.prevent event handler to intercept form submission -->
          <form @submit.prevent="handleSubmit">
            <!-- SECTION 1: Parent/Contact Information -->
            <fieldset class="mb-4">
              <legend class="h5 mb-3 border-bottom pb-2">1. Vos informations de contact</legend>
              <div class="row g-3">
                <div class="col-md-6">
                  <label for="parentNom" class="form-label">Nom</label>
                  <input type="text" id="parentNom" class="form-control" v-model="formData.parents.lastname" required>
                </div>
                <div class="col-md-6">
                  <label for="parentPrenom" class="form-label">Prénom</label>
                  <input type="text" id="parentPrenom" class="form-control" v-model="formData.parents.firstname"
                         required>
                </div>
                <div class="col-md-6">
                  <label for="parentEmail" class="form-label">Email</label>
                  <input type="email" id="parentEmail" class="form-control" v-model="formData.parents.email" required>
                </div>
                <div class="col-md-6">
                  <label for="parentTel" class="form-label">Téléphone</label>
                  <input type="tel" id="parentTel" class="form-control" v-model="formData.parents.phone" required>
                </div>
                <div class="col-12">
                  <label for="parentAdresse" class="form-label">Adresse</label>
                  <input type="text" id="parentAdresse" class="form-control" v-model="formData.parents.address">
                </div>
                <div class="col-md-8">
                  <label for="parentVille" class="form-label">Ville</label>
                  <input type="text" id="parentVille" class="form-control" v-model="formData.parents.city">
                </div>
                <div class="col-md-4">
                  <label for="parentCP" class="form-label">Code Postal</label>
                  <input type="text" id="parentCP" class="form-control" v-model="formData.parents.zipCode">
                </div>
              </div>
            </fieldset>

            <!-- SECTION 2: Child & Contract Information -->
            <fieldset class="mb-4">
              <legend class="h5 mb-3 border-bottom pb-2">2. Informations sur l'enfant et le contrat</legend>
              <div class="row g-3">
                <div class="col-md-6">
                  <label for="enfantNom" class="form-label">Nom de l'enfant</label>
                  <input type="text" id="enfantNom" class="form-control" v-model="formData.child.firstname" required>
                </div>
                <div class="col-md-6">
                  <label for="enfantNaissance" class="form-label">Date de naissance</label>
                  <input type="date" id="enfantNaissance" class="form-control" v-model="formData.child.bithdate"
                         required>
                </div>
                <div class="col-md-6">
                  <label for="contratDebut" class="form-label">Date de début de contrat souhaitée</label>
                  <input type="date" id="contratDebut" class="form-control" v-model="formData.contract.start" required>
                </div>
                <div class="col-md-6">
                  <label for="contratFin" class="form-label">Date de fin de contrat estimée</label>
                  <input type="date" id="contratFin" class="form-control" v-model="formData.contract.end">
                </div>
              </div>
            </fieldset>

            <!-- SECTION 3: Care Days -->
            <fieldset class="mb-4">
              <legend class="h5 mb-3 border-bottom pb-2">3. Jours d'accueil souhaités</legend>
              <div class="d-flex flex-wrap justify-content-center">
                <div v-for="jour in jours" :key="jour" class="form-check form-check-inline me-3 mb-2">
                  <input class="form-check-input" type="checkbox" :id="`check-${jour}`" :value="jour"
                         v-model="formData.days">
                  <label class="form-check-label" :for="`check-${jour}`">{{ jour }}</label>
                </div>
              </div>
            </fieldset>

            <!-- Validation and submission -->
            <div class="form-check mb-4">
              <input class="form-check-input" type="checkbox" id="consent" v-model="formData.agreement" required>
              <label class="form-check-label" for="consent">
                J'autorise ce site à conserver mes données transmises via ce formulaire.
              </label>
            </div>

            <div class="text-center">
              <button type="submit" class="btn btn-primary btn-lg">
                <i class="bi bi-send-fill me-2"></i>Envoyer ma demande
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import Vue, { ref } from "vue";
import { send } from "emailjs-com";
import { useToast } from "vue-toastification";

const alreadySummit = ref(false);
const toast = useToast();
// Structure all form data in a single reactive object
const formData = ref({
  parents: {
    lastname: "",
    firstname: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    zipCode: ""
  },
  child: {
    firstname: "",
    bithdate: ""
  },
  contract: {
    start: "",
    end: ""
  },
  days: [], // Array to store checked days
  agreement: false
});

// List of days to generate checkboxes
const jours = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi"];

const handleSubmit = () => {
  if (formData.value.agreement === true && alreadySummit.value === false) {
    let dayText = "";
    alreadySummit.value = true;
    formData.value.days.forEach(day => {
      dayText += `- ${day} - `;
    });

    send(
      "service_vnvta9q",
      "template_5jzl58n",
      {
        lastname: formData.value.parents.lastname,
        firstname: formData.value.parents.firstname,
        email: formData.value.parents.email,
        phone: formData.value.parents.phone,
        adress: formData.value.parents.address,
        city: formData.value.parents.city,
        postalCode: formData.value.parents.zipCode,
        childName: formData.value.child.firstname,
        childBirthdate: formData.value.child.bithdate,
        start: formData.value.contract.start,
        end: formData.value.contract.end,
        week: dayText
      },
      process.env.VUE_APP_USER_ID
    )
      .then(() => {
        toast.success(
          "Votre pré-inscription a bien été transmise à l'équipe BébéBulle"
        );
      }).catch(() => {
      toast.error(
        "Une erreur est survenue lors de la transmission de vos informations."
      );
    }).finally(() => {
      alreadySummit.value = false;
      formData.value = {
        parents: {
          lastname: "",
          firstname: "",
          email: "",
          phone: "",
          address: "",
          city: "",
          zipCode: ""
        },
        child: {
          firstname: "",
          bithdate: ""
        },
        contract: {
          start: "",
          end: ""
        },
        days: [], // Array to store checked days
        agreement: false
      };
    });
  }
};
</script>

<style scoped>
/* Improves form field appearance */
.form-control, .form-select {
  padding: 0.75rem 1rem;
}

legend {
  font-family: 'Poppins', sans-serif;
  font-weight: 700;
}
</style>
